import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { User, AuthState, LoginRequest, RegisterRequest, LoginResponse } from '@/types'
import { authApi } from '@/services/api'
import toast from 'react-hot-toast'
import { logger } from '@/utils/logger'
import { authKeys } from '@/lib/queryKeys'
import { useTranslation } from 'react-i18next'

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User } }
  | { type: 'AUTH_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true }
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: null,
        isAuthenticated: true,
        isLoading: false,
      }
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      }
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      }
    default:
      return state
  }
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => Promise<void>
  updateUser: (user: User) => void
  updateToken: (token: string) => void  // kept for interface compatibility, no-op with cookie auth
  refreshUser: () => Promise<void>
  loginWithGoogle: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { t } = useTranslation('toasts')
  const [state, dispatch] = useReducer(authReducer, initialState)
  const queryClient = useQueryClient()

  // #1746: true from logout until a profile lands in the cache again (login,
  // register, refreshUser, updateUser). logout calls queryClient.clear(), and
  // the still-mounted profile query below rebuilds on the next render. If it
  // were enabled it would refetch /auth/profile straight away, and when the
  // backend logout failed before the server cleared the httpOnly cookie, that
  // refetch succeeds and logs the user back in right after they were told they
  // logged out. So logout disables the query before it clears the cache.
  //
  // A ref, not state: the query must be disabled the moment the cache is
  // cleared, not at the next render that happens to apply a state update.
  // React can render in between without that update. React Query's own
  // notifications (e.g. the logout mutation's state change) re-render through
  // useSyncExternalStore at sync priority, which skips the lower-priority
  // setState and dispatch calls made from the mutation callbacks. If such a
  // notification lands between onError/onSuccess and React's render of those
  // updates, that render sees the cleared cache with the query still enabled
  // and refetches (#1746). `enabled` below reads the ref whenever React Query
  // decides whether to fetch, so no render can miss the logout.
  const loggedOutRef = useRef(false)

  // Attempt to fetch the user profile on startup.
  // Success means the httpOnly cookie is present and valid; 401 means unauthenticated.
  const { data: user, isLoading: profileLoading, error: profileError } = useQuery({
    queryKey: authKeys.profile(),
    queryFn: async () => {
      return await authApi.getProfile()
    },
    enabled: () => !loggedOutRef.current,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  })

  // Update auth state when user profile is loaded
  useEffect(() => {
    if (user) {
      logger.info('AuthContext - User loaded successfully via React Query')
      // A signed-in profile is back in the cache, so re-enable the query.
      // The data is fresh (staleTime), so this does not trigger a refetch.
      loggedOutRef.current = false
      dispatch({ type: 'AUTH_SUCCESS', payload: { user } })
    } else if (profileError) {
      logger.error('AuthContext - Error loading user profile', profileError)
      dispatch({ type: 'AUTH_FAILURE' })
    }
  }, [user, profileError])

  // Update loading state
  useEffect(() => {
    if (profileLoading) {
      dispatch({ type: 'AUTH_START' })
    }
  }, [profileLoading])

  // Login mutation using React Query
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      // Do NOT log credentials.email — it's PII and ends up in browser devtools
      // logs (and any forwarded telemetry). Status-only logging is sufficient.
      logger.debug('AuthContext - Login attempt')
      const response = await authApi.login(credentials)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Login failed')
      }
      return response.data as LoginResponse
    },
    onSuccess: async (loginData) => {
      const { user } = loginData
      logger.info('AuthContext - Login successful', { userId: user?.id })

      // Defensive: scrub impersonation sessionStorage flags. They
      // persist across logout (sessionStorage doesn't auto-clear
      // when the auth cookie is removed) so a previous session
      // that logged out mid-impersonation leaves stale flags
      // behind. The ImpersonationProvider re-hydrates from these
      // on mount and would render the yellow 'You are
      // impersonating...' banner on top of a clean fresh login.
      // Always clear at login time — the new session is a fresh
      // identity by definition.
      sessionStorage.removeItem('impersonation_active')
      sessionStorage.removeItem('impersonated_user')
      sessionStorage.removeItem('original_admin')
      // Reset the pending-affiliations notification dismiss latch so a
      // fresh login always re-shows the prompt even when the same tab
      // is reused (sessionStorage persists across logout-back-in flows).
      sessionStorage.removeItem('affiliation-notif-dismissed')

      // Fetch complete profile — the backend has set the httpOnly cookie
      try {
        const completeProfile = await authApi.getProfile()
        queryClient.setQueryData(authKeys.profile(), completeProfile)
        dispatch({ type: 'AUTH_SUCCESS', payload: { user: completeProfile } })
      } catch (error) {
        logger.warn('Failed to fetch complete profile after login', { error })
        if (user) {
          queryClient.setQueryData(authKeys.profile(), user)
          dispatch({ type: 'AUTH_SUCCESS', payload: { user } })
        }
      }

      toast.success(t('success.loginSuccessful'))
    },
    onError: (error: any) => {
      logger.error('AuthContext - Login error', { error })
      dispatch({ type: 'AUTH_FAILURE' })

      // Don't show toast for MFA required — LoginPage handles this
      const isMfaRequired = error?.response?.status === 428 ||
        error?.response?.data?.code === 'MFA_REQUIRED' ||
        error?.code === 'MFA_REQUIRED'
      if (isMfaRequired) return

      const translatedMsg = t('error.loginFailed')
      const errorMessage =
        error?.error ||
        error?.message ||
        (error instanceof Error ? error.message : null) ||
        translatedMsg ||
        'Login failed'
      toast.error(errorMessage)
    },
  })

  const login = async (credentials: LoginRequest) => {
    dispatch({ type: 'AUTH_START' })
    await loginMutation.mutateAsync(credentials)
  }

  const register = async (data: RegisterRequest) => {
    try {
      dispatch({ type: 'AUTH_START' })
      const response = await authApi.register(data)

      if (response.success && response.data) {
        const regData = response.data as any
        const user = regData.user

        if (user) {
          // The backend sets the httpOnly cookie on registration; just store the user
          queryClient.setQueryData(authKeys.profile(), user)
          dispatch({ type: 'AUTH_SUCCESS', payload: { user } })
        }

        toast.success(t('success.registrationSuccessful'))
      } else {
        throw new Error(response.message || t('error.registrationFailed'))
      }
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' })
      toast.error(error instanceof Error ? error.message : t('error.registrationFailed'))
      throw error
    }
  }

  // Logout mutation using React Query
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Call backend logout API — the server clears the httpOnly cookie
      await authApi.logout()
    },
    onSuccess: () => {
      // Disable the profile query before clearing (#1746, see loggedOutRef).
      loggedOutRef.current = true
      queryClient.clear()
      // sessionStorage doesn't clear on logout by itself; the
      // ImpersonationProvider re-hydrates from these keys on its
      // next mount, so without this scrub the user gets stuck
      // showing the impersonation banner the next time they log
      // back in. Clear them here regardless of whether the
      // user was actively impersonating.
      sessionStorage.removeItem('impersonation_active')
      sessionStorage.removeItem('impersonated_user')
      sessionStorage.removeItem('original_admin')
      sessionStorage.removeItem('affiliation-notif-dismissed')
      dispatch({ type: 'LOGOUT' })
      toast.success(t('success.loggedOut'))
    },
    onError: (error) => {
      // Even if backend logout fails, clear local state
      logger.warn('Backend logout failed', { error })
      // The server may not have cleared the cookie, so a profile refetch here
      // would log the user back in. Disable the query first (#1746).
      loggedOutRef.current = true
      queryClient.clear()
      sessionStorage.removeItem('impersonation_active')
      sessionStorage.removeItem('impersonated_user')
      sessionStorage.removeItem('original_admin')
      sessionStorage.removeItem('affiliation-notif-dismissed')
      dispatch({ type: 'LOGOUT' })
      // Not "Logged out successfully": the client cannot see the httpOnly
      // cookie, so it cannot tell whether the server ended the session. If it
      // did not, a reload signs the same user back in. Warn instead, so nobody
      // walks away from a shared machine that still holds a live session.
      toast(t('warning.logoutIncomplete'), { icon: '⚠️', duration: 8000 })
    },
  })

  // Resolves on both paths. When the backend call fails, onError above has
  // already logged the client out and warned the user, so there is nothing
  // left for a caller to handle. Rethrowing would only surface as an unhandled
  // rejection from the click handlers that call this without a catch (Header,
  // OnboardingStatusPage).
  const logout = async () => {
    try {
      await logoutMutation.mutateAsync()
    } catch {
      // Handled in logoutMutation.onError.
    }
  }

  const updateUser = (user: User) => {
    queryClient.setQueryData(authKeys.profile(), user)
    dispatch({ type: 'UPDATE_USER', payload: user })
  }

  // Kept for interface compatibility — token management is now handled server-side via httpOnly cookies
  const updateToken = (_token: string) => {
    // no-op: tokens are managed by the server via httpOnly cookies
  }

  const refreshUser = async () => {
    try {
      await queryClient.invalidateQueries({ queryKey: authKeys.profile() })
      const user = await queryClient.fetchQuery({
        queryKey: authKeys.profile(),
        queryFn: async () => {
          return await authApi.getProfile()
        },
      })
      if (user) {
        dispatch({ type: 'UPDATE_USER', payload: user })
      }
    } catch (error) {
      logger.error('Failed to refresh user data', error)
    }
  }

  const loginWithGoogle = () => {
    window.location.href = authApi.googleAuth()
  }

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    updateToken,
    refreshUser,
    loginWithGoogle,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}